import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './DepositManagement.css';

function DepositManagement() {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(Date.now());

  // Auto-refresh interval in seconds
  const refreshInterval = 10; // 30 seconds

  useEffect(() => {
    fetchDeposits();
    
    // Set up auto-refresh interval
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchDeposits();
      }
    }, refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [currentPage, statusFilter]);

  const fetchDeposits = async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://trcsite.net/api/admin/deposits?page=${currentPage}&limit=20&status=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDeposits(response.data.deposits);
      setTotalPages(response.data.totalPages);
      setLastUpdate(Date.now());
    } catch (error) {
      console.error('Error fetching deposits:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchDeposits();
  };

  const approveDeposit = async (depositId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`https://trcsite.net/api/admin/deposits/${depositId}/approve`, 
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchDeposits(); // Refresh the list
      alert('Deposit approved successfully');
    } catch (error) {
      console.error('Error approving deposit:', error);
      alert('Failed to approve deposit');
    }
  };

  const rejectDeposit = async (depositId) => {
    const reason = prompt('Please enter rejection reason:');
    if (!reason) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`https://trcsite.net/api/admin/deposits/${depositId}/reject`, 
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchDeposits(); // Refresh the list
      alert('Deposit rejected successfully');
    } catch (error) {
      console.error('Error rejecting deposit:', error);
      alert('Failed to reject deposit');
    }
  };

  // Format time since last update
  const getTimeSinceUpdate = () => {
    const seconds = Math.floor((Date.now() - lastUpdate) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    return `${Math.floor(seconds / 60)}m ago`;
  };

  if (loading) {
    return <div className="loading">Loading deposits...</div>;
  }

  return (
    <div className="management-section">
      <div className="section-header">
        <div className="header-left">
          <h2>Deposit Management</h2>
          <div className="update-info">
            <span className="last-update">Last update: {getTimeSinceUpdate()}</span>
            <span className="auto-refresh">Auto-refresh: {refreshInterval}s</span>
          </div>
        </div>
        
        <div className="header-actions">
          <div className="filters">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-filter"
            >
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
              <option value="">All</option>
            </select>
          </div>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
            title={`Auto-refreshes every ${refreshInterval} seconds`}
          >
            <span className="refresh-icon">
              {refreshing ? '⏳' : '🔄'}
            </span>
            <span className="refresh-text">
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </span>
            <span className="refresh-interval">({refreshInterval}s)</span>
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="deposits-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Currency</th>
              <th>Amount</th>
              <th>TX Hash</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {deposits.length > 0 ? (
              deposits.map((deposit) => (
                <tr key={deposit.id}>
                  <td>
                      <div className="user-name">{deposit.user_name}</div>
                      <div className="user-email">{deposit.user_uid}</div>                      
                  </td>
                  <td className="currency-cell">{deposit.currency}</td>
                  <td className="amount-cell">{deposit.amount}</td>
                  <td className="tx-hash">
                    {deposit.tx_hash ? (
                      <span title={deposit.tx_hash}>
                        {deposit.tx_hash.slice(0, 8)}...{deposit.tx_hash.slice(-8)}
                      </span>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td>
                    <span className={`status-badge status-${deposit.status}`}>
                      {deposit.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="date-cell">
                    {new Date(deposit.created_at).toLocaleDateString()}
                    <div className="time">
                      {new Date(deposit.created_at).toLocaleTimeString()}
                    </div>
                  </td>
                  <td>
                    {deposit.status === 'pending' && (
                      <div className="action-buttons-dp">
                        <button
                          onClick={() => approveDeposit(deposit.id)}
                          className="btn-approve"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => rejectDeposit(deposit.id)}
                          className="btn-reject"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                    {deposit.status !== 'pending' && (
                      <span className="no-actions">No actions</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="no-data">
                  <div className="no-data-content">
                    <span className="no-data-icon">📭</span>
                    <span>No deposits found</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="pagination-btn prev"
          >
            Previous
          </button>
          
          <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="pagination-btn next"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default DepositManagement;